import { useEffect, useRef } from 'react'
import Image from 'next/image'
import { trackHeroVideoAutoplayStarted } from '../events'

const POSTER_IMG_URL =
  'https://static.masterclass.com/pen5690/hp_inno_poster.png'

const AV1_360P = 'https://static.masterclass.com/pen5690/opt/hero-360.av1.webm'
const H264_360P = 'https://static.masterclass.com/pen5690/opt/hero-360.h264.mp4'
const VIDEO_SRC = 'https://static.masterclass.com/pen5690/hpinno_hero.mp4'

export const HeroVideo = ({
  className,
  prefersReducedMotion,
}: {
  className: string
  prefersReducedMotion: boolean
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const posterRef = useRef<HTMLImageElement>(null)
  const hasTrackedAutoplay = useRef(false)

  const opacityTransition = 'Dm2akz'
  const hideClass = 'M8f4yx'

  useEffect(() => {
    const video = videoRef.current
    if (!video) return () => {}

    const onPlaying = () => {
      // Cross-fade: hide poster and show video simultaneously
      posterRef.current?.classList.add(hideClass)

      if (!hasTrackedAutoplay.current) {
        hasTrackedAutoplay.current = true
        trackHeroVideoAutoplayStarted()
      }
    }

    // Safari: Check if video is already playing when useEffect runs
    const HAVE_CURRENT_DATA = 2
    if (!video.paused && !video.ended && video.readyState > HAVE_CURRENT_DATA) {
      onPlaying()
    }

    video?.addEventListener('playing', onPlaying)

    return () => {
      video?.removeEventListener('playing', onPlaying)
    }
  }, [videoRef])

  return (
    <>
      <style>
        {`
          .${opacityTransition} { transition: opacity 0.3s ease; }
          .${hideClass} { opacity: 0; pointer-events: none; }
        `}
      </style>
      <Image
        ref={posterRef}
        className={`${className} ${opacityTransition}`}
        src={POSTER_IMG_URL}
        alt=''
        width={1920}
        height={1080}
        sizes='100vw'
        priority
        loading='eager'
        fetchPriority='high'
        decoding='sync'
        style={{ zIndex: 0 }}
      />
      <video
        ref={videoRef}
        className={`${className} ${opacityTransition}`}
        preload='none'
        autoPlay={!prefersReducedMotion}
        // eslint-disable-next-line react/no-unknown-property
        fetchpriority='low'
        playsInline
        muted
        loop
        style={{ zIndex: -1, opacity: 1 }}
      >
        <source
          media='(max-width: 600px)'
          src={AV1_360P}
          type='video/webm; codecs=av01'
        />
        <source
          media='(max-width: 600px)'
          src={H264_360P}
          type='video/mp4; codecs=avc1'
        />
        {/* full size for large screens, plus fallback for unsupported browsers */}
        <source src={VIDEO_SRC} type='video/mp4' />
      </video>
    </>
  )
}
